import { useEffect, useState } from "react";
import { ApiError, api, type AdminUser, type Rol } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AyudaVista from "@/ui/AyudaVista";
import { ayudaAdmin } from "@/components/ayuda/vistas/admin";
import Toggle from "@/ui/Toggle";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";

export default function Admin() {
  const me = useAuth((s) => s.user);
  const toast = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [resetting, setResetting] = useState<AdminUser | null>(null);

  async function refresh() {
    const list = await api.get<AdminUser[]>("/api/admin/users");
    setUsers(list);
  }

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  async function toggleActivo(u: AdminUser) {
    if (u.id === me?.id) {
      toast.error("No puedes desactivarte a ti mismo");
      return;
    }
    try {
      if (u.activo) {
        await api.del(`/api/admin/users/${u.id}`);
        toast.success("Usuario desactivado", u.email);
      } else {
        await api.patch<AdminUser>(`/api/admin/users/${u.id}`, { activo: true });
        toast.success("Usuario reactivado", u.email);
      }
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo actualizar", msg);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">Administración</h1>
          <AyudaVista titulo="Administración" {...ayudaAdmin()} />
        </div>
        <button onClick={() => setCreateOpen(true)} className="btn-primary">
          + Nuevo usuario
        </button>
      </div>

      <div className="card overflow-x-auto">
        {loading ? (
          <p className="text-sm text-zinc-500">Cargando...</p>
        ) : users.length === 0 ? (
          <p className="text-sm text-zinc-500">No hay usuarios.</p>
        ) : (
          <>
            {/* Mobile: cards */}
            <ul className="lg:hidden divide-y divide-ink-700">
              {users.map((u) => (
                <li key={u.id} className={`py-2.5 space-y-1.5 ${u.activo ? "" : "opacity-50"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-mono text-accent-400 text-sm break-all">{u.email}</span>
                    <span className="shrink-0"><RolPill rol={u.rol} /></span>
                  </div>
                  <div className="text-sm text-zinc-200">{u.nombre}</div>
                  <div className="flex items-center gap-3 text-xs text-zinc-400">
                    <button
                      onClick={() => toggleActivo(u)}
                      disabled={u.id === me?.id}
                      className={`px-2 py-1 text-xs rounded border ${
                        u.activo
                          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                          : "border-zinc-600 bg-zinc-800/50 text-zinc-400"
                      } disabled:opacity-50`}
                    >
                      {u.activo ? "Activo" : "Inactivo"}
                    </button>
                    <span>{u.notif_email ? "✉" : "—"} {u.notif_telegram ? "tg" : ""}</span>
                    <span className="ml-auto">{new Date(u.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => setEditing(u)} className="btn-ghost text-xs flex-1 border border-ink-600">
                      Editar
                    </button>
                    <button onClick={() => setResetting(u)} className="btn-ghost text-xs flex-1 border border-ink-600">
                      Restablecer contraseña
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/* Desktop: tabla */}
            <table className="hidden lg:table w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 text-left">
                <tr>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Nombre</th>
                  <th className="py-2 pr-4">Rol</th>
                  <th className="py-2 pr-4">Activo</th>
                  <th className="py-2 pr-4">Notif</th>
                  <th className="py-2 pr-4">Creado</th>
                  <th className="py-2 pr-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-700">
                {users.map((u) => (
                  <tr key={u.id} className={u.activo ? "" : "opacity-50"}>
                    <td className="py-2 pr-4 font-mono text-accent-400">{u.email}</td>
                    <td className="py-2 pr-4">{u.nombre}</td>
                    <td className="py-2 pr-4">
                      <RolPill rol={u.rol} />
                    </td>
                    <td className="py-2 pr-4">
                      <button
                        onClick={() => toggleActivo(u)}
                        disabled={u.id === me?.id}
                        className={`px-2 py-0.5 text-xs rounded border ${
                          u.activo
                            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                            : "border-zinc-600 bg-zinc-800/50 text-zinc-400"
                        } disabled:opacity-50`}
                      >
                        {u.activo ? "Activo" : "Inactivo"}
                      </button>
                    </td>
                    <td className="py-2 pr-4 text-xs text-zinc-400">
                      {u.notif_email ? "✉" : "—"} {u.notif_telegram ? "tg" : ""}
                    </td>
                    <td className="py-2 pr-4 text-xs text-zinc-400">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-2 pr-4 text-right space-x-1">
                      <button onClick={() => setEditing(u)} className="btn-ghost text-xs">
                        Editar
                      </button>
                      <button
                        onClick={() => setResetting(u)}
                        className="btn-ghost text-xs"
                        aria-label="Restablecer contraseña"
                      >
                        Pass
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refresh}
      />
      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
      {resetting && (
        <ResetPasswordModal
          user={resetting}
          onClose={() => setResetting(null)}
        />
      )}
    </div>
  );
}

function RolPill({ rol }: { rol: Rol }) {
  const cls: Record<Rol, string> = {
    admin: "bg-red-500/10 text-red-300 border-red-500/30",
    soporte: "bg-sky-500/10 text-sky-300 border-sky-500/30",
    usuario: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${cls[rol]}`}>
      {rol}
    </span>
  );
}

function CreateUserModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}) {
  const toast = useToast();
  const [form, setForm] = useState({
    email: "",
    nombre: "",
    password: "",
    rol: "soporte" as Rol,
    notif_email: true,
    notif_telegram: false,
  });
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (form.password.length < 8) {
      toast.error("Contraseña muy corta", "Mínimo 8 caracteres");
      return;
    }
    setSubmitting(true);
    try {
      await api.post<AdminUser>("/api/admin/users", form);
      toast.success("Usuario creado", form.email);
      await onCreated();
      onClose();
      setForm({ email: "", nombre: "", password: "", rol: "soporte", notif_email: true, notif_telegram: false });
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error desconocido";
      toast.error("No se pudo crear", msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Nuevo usuario"
      description="El usuario podrá entrar con email + contraseña. Su rol determina sus permisos."
      footer={
        <>
          <button onClick={onClose} className="btn-ghost">Cancelar</button>
          <button onClick={submit} className="btn-primary" disabled={submitting}>
            {submitting ? "Creando..." : "Crear"}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Email">
          <input
            type="email"
            className="input"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="alguien@arknex.net"
          />
        </Field>
        <Field label="Nombre">
          <input
            type="text"
            className="input"
            value={form.nombre}
            onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            placeholder="Nombre completo"
          />
        </Field>
        <Field label="Contraseña (mín. 8)">
          <input
            type="text"
            className="input font-mono"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Contraseña inicial"
          />
        </Field>
        <Field label="Rol">
          <select
            className="input"
            value={form.rol}
            onChange={(e) => setForm({ ...form, rol: e.target.value as Rol })}
          >
            <option value="admin">admin — gestiona todo</option>
            <option value="soporte">soporte — gestiona casos</option>
            <option value="usuario">usuario — solo lectura</option>
          </select>
        </Field>
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-4 text-sm">
          <Toggle
            checked={form.notif_email}
            onChange={(v) => setForm({ ...form, notif_email: v })}
            label="Notificar por email"
            className="text-sm"
          />
          <Toggle
            checked={form.notif_telegram}
            onChange={(v) => setForm({ ...form, notif_telegram: v })}
            label="Notificar por Telegram"
            className="text-sm"
          />
        </div>
      </div>
    </Modal>
  );
}

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const toast = useToast();
  const [nombre, setNombre] = useState(user.nombre);
  const [rol, setRol] = useState<Rol>(user.rol);
  const [notifEmail, setNotifEmail] = useState(user.notif_email);
  const [notifTg, setNotifTg] = useState(user.notif_telegram);
  const [tgChat, setTgChat] = useState(user.telegram_chat_id ?? "");
  const [aiCom, setAiCom] = useState(user.ai_comentarios_enabled);
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      await api.patch<AdminUser>(`/api/admin/users/${user.id}`, {
        nombre,
        rol,
        notif_email: notifEmail,
        notif_telegram: notifTg,
        telegram_chat_id: tgChat || null,
        ai_comentarios_enabled: aiCom,
      });
      toast.success("Usuario actualizado", user.email);
      await onSaved();
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo actualizar", msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={true}
      onOpenChange={(v) => !v && onClose()}
      title={`Editar ${user.email}`}
      footer={
        <>
          <button onClick={onClose} className="btn-ghost">Cancelar</button>
          <button onClick={submit} className="btn-primary" disabled={submitting}>
            {submitting ? "Guardando..." : "Guardar"}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Nombre">
          <input className="input" value={nombre} onChange={(e) => setNombre(e.target.value)} />
        </Field>
        <Field label="Rol">
          <select
            className="input"
            value={rol}
            onChange={(e) => setRol(e.target.value as Rol)}
          >
            <option value="admin">admin</option>
            <option value="soporte">soporte</option>
            <option value="usuario">usuario</option>
          </select>
        </Field>
        <div className="flex flex-col gap-2 text-sm">
          <Toggle checked={notifEmail} onChange={setNotifEmail} label="Notificar por email" className="text-sm" />
          <Toggle checked={notifTg} onChange={setNotifTg} label="Notificar por Telegram" className="text-sm" />
          <Toggle checked={aiCom} onChange={setAiCom} label="Asistente de IA en correos (Generar comentario)" className="text-sm" />
        </div>
        <Field label="Telegram chat_id (opcional)">
          <input
            className="input font-mono"
            value={tgChat}
            onChange={(e) => setTgChat(e.target.value)}
            placeholder="ej. 713690492"
          />
        </Field>
      </div>
    </Modal>
  );
}

function ResetPasswordModal({
  user,
  onClose,
}: {
  user: AdminUser;
  onClose: () => void;
}) {
  const toast = useToast();
  const [pass, setPass] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (pass.length < 8) {
      toast.error("Contraseña muy corta", "Mínimo 8 caracteres");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/api/admin/users/${user.id}/password`, { password: pass });
      toast.success("Contraseña actualizada", user.email);
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo actualizar", msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={true}
      onOpenChange={(v) => !v && onClose()}
      title={`Restablecer contraseña de ${user.email}`}
      description="El usuario deberá entrar con esta nueva contraseña. La anterior queda invalidada."
      size="sm"
      footer={
        <>
          <button onClick={onClose} className="btn-ghost">Cancelar</button>
          <button onClick={submit} className="btn bg-red-600 hover:bg-red-500 text-white" disabled={submitting}>
            {submitting ? "Actualizando..." : "Resetear"}
          </button>
        </>
      }
    >
      <Field label="Nueva contraseña (mín. 8)">
        <input
          type="text"
          className="input font-mono"
          value={pass}
          onChange={(e) => setPass(e.target.value)}
          placeholder="Contraseña nueva"
          autoFocus
        />
      </Field>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-zinc-400">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
